import Phaser from 'phaser';
import { GAME } from '../config';
import { loadProgress, saveProgress, recordBossDefeated, recordWeaponUnlocked, addWeaponShards } from '../../shared/progress';
import { weaponUnlockForBoss, weaponTypeById, TOTAL_BOSS_COUNT } from '../progression/boss-weapon-unlocks';
import { weaponMetaById } from '../weapons/registry';
import { mainWeaponForBoss, victoryChestUnlock } from '../weapons/weapon-loot';
import { addCommanderXp, computeCommanderXp } from '../progression/commander';
import { computeMetaCoins } from '../progression/meta-economy';
import { rewardMultiplierForStage } from '../data/chapter-difficulty';
import { consumeActiveCampaignStage } from '../progression/active-campaign-stage';
import { markStageCleared } from '../progression/battle-campaign-progress';
import type { StageClearResult } from '../progression/battle-campaign-progress';
import { SoundPlayer } from '../audio/sounds';
import { spawnConfetti } from '../fx/confetti';
import {
  revealEmblem,
  emblemTextureKey,
  diffSkillLevels,
  showLevelUpFloats,
  loadLastSkillLevelsSnapshot,
} from '../fx/emblem-reveal';
import { pickDisplayFaction } from '../entities/sprite-keys';
import { FACTION_BURST_COLOR, FACTION_BURST_FALLBACK_COLOR } from './factionColors';
import { tickValue } from '../fx/tick-value';
import { computeResultSections, REWARDS_MAX_VISIBLE } from '../fx/result-layout';
import { commanderXpToNextLevel, COMMANDER_LEVEL_THRESHOLDS } from '../progression/commander';
import { getCurrentProfileId } from '../../shared/profile';
import type { FactionId, Subject } from '../../shared/types';

/* ===========================================================================
 * W8 PR-3 — 學習與遊戲分離鐵則(CLAUDE.md 最高優先 / spec §4.2):
 *   ResultScene 屬「遊戲端」,絕對不得 `import` 任何 src/learn 模組,
 *   不得呼叫任何 quiz/question 相關 API。spec test case 6 以 static import
 *   掃描本檔案內容驗證:若任何 (學習路徑) substring 出現即 fail。
 * 之所以把鐵則寫在檔頭:後續 maintainer 若不小心 import learn 模組,review
 *   時可直接看到禁令,避免 PR 通過 test 但破壞分層原則。
 *
 * 雙軌進度鐵則(PROGRESSION_AND_ACCOUNTS.md §3.4 / §5):戰鬥勝利**只**發
 *   「指揮官經驗」(addCommanderXp),屬玩遊戲軌(帳號廣度)。本檔
 *   **絕不**呼叫 addFreeXP / allocateFreeXP / addFactionXP —— 玩遊戲不得灌
 *   核心戰力修煉值(那是學習軌專屬)。static-source 測試掃本檔驗證。
 * =========================================================================== */

export interface BattleStats {
  wave: number;
  killed: number;
  survivedSec: number;
  timestamp: number;
}

/** 舊版單一全域鍵(profile 分離前)。讀取時當 fallback,寫入時不再用。 */
const LEGACY_BATTLE_STATS_KEY = 'eternal-fortress.last-battle-stats';

/**
 * 上一場戰績儲存鍵 — 依 profile 區隔(R-004 A5)。
 * 多 profile 共用同一裝置時,「本場 vs 上次」必須各自比各自的上一場,
 * 不可串台。getCurrentProfileId 失敗(headless / 無 localStorage)→ 退回
 * 'default',行為與單 profile 一致。
 */
function battleStatsKey(): string {
  let id = 'default';
  try { id = getCurrentProfileId(); } catch { /* headless → default */ }
  return `${LEGACY_BATTLE_STATS_KEY}.${id}`;
}

export function loadLastBattleStats(): BattleStats | null {
  try {
    // 先讀 profile 專屬鍵;沒有再退回舊全域鍵(一次性相容,不回寫)。
    const raw = localStorage.getItem(battleStatsKey())
      ?? localStorage.getItem(LEGACY_BATTLE_STATS_KEY);
    return raw ? (JSON.parse(raw) as BattleStats) : null;
  } catch {
    return null;
  }
}

export function saveBattleStats(stats: BattleStats): void {
  try {
    localStorage.setItem(battleStatsKey(), JSON.stringify(stats));
  } catch {
    // localStorage unavailable — ignore
  }
}

/**
 * W8 PR-3 — 去戲謔失敗鼓勵語(boss memory feedback_learn_pro_mode 2026-05-23
 * 「教學是要專業的」要求)。原本含「魔王」「修煉值」等戲謔/門派詞已移除,
 * 全部統一為通用、正面、去遊戲語境的學習鼓勵句。
 */
const ENCOURAGE_PHRASES = [
  '再試一次,你快做到了!',
  '繼續努力,距離成功不遠了!',
  '每次練習都讓你更進步!',
  '堅持下去,你一定可以!',
  '這次的經驗讓下次更接近成功!',
];

export interface XPBreakdownEntry {
  label: string;   // e.g. '波次完成獎勵'
  amount: number;  // e.g. 20
}

/**
 * R-004 A5 — 「本場 vs 上次」單列對比資料(純資料,供測試斷言 + render 共用)。
 * direction: 'up' 進步 / 'down' 退步 / 'same' 持平。
 */
export interface StatComparison {
  label: string;       // '波次' / '消滅' / '得分'
  current: number;
  previous: number;
  delta: number;       // current − previous
  direction: 'up' | 'down' | 'same';
  arrow: '↑' | '↓' | '—';
}

/**
 * 純函式:把本場 stats 與上一場 stats 比成 ↑↓ 對比列。
 * `prev` 為 null(史上第一場)→ 回空陣列(無對比可顯示)。
 * 比較欄位:波次(wave)、消滅(killed)、得分(score,缺值視為 0)。
 */
export function buildStatComparisons(
  current: { wave: number; killed: number; score?: number },
  prev: { wave: number; killed: number; score?: number } | null,
): StatComparison[] {
  if (!prev) return [];
  const mkRow = (label: string, cur: number, old: number): StatComparison => {
    const delta = cur - old;
    const direction: StatComparison['direction'] = delta > 0 ? 'up' : delta < 0 ? 'down' : 'same';
    const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '—';
    return { label, current: cur, previous: old, delta, direction, arrow };
  };
  return [
    mkRow('波次', current.wave, prev.wave),
    mkRow('消滅', current.killed, prev.killed),
    mkRow('得分', current.score ?? 0, prev.score ?? 0),
  ];
}

/**
 * W8 PR-3 — Mastery box 升級 entry。
 * `kpId` 為知識點 id(spec 用詞;ResultScene 不直接連 question API,只接收
 * caller 已 diff 完的結果)。`fromBox`/`toBox` 為 SM-2-style box index
 * (整數,0-5)。upgraded 才會列入此陣列;未動者由 caller 排除。
 */
export interface MasteryBoxUpgrade {
  kpId: string;
  fromBox: number;
  toBox: number;
}

/**
 * W8 PR-3 — 段位(subjectLevel)變化。spec §4.3 fromTier/toTier 為字串
 * tier code(例如 'bronze' → 'silver')。為與既有 subject 段位 UI 呼應,
 * 同時支援 numeric Lv form by passing string '4' → '5'。
 */
export interface SubjectLevelUpgrade {
  fromTier: string;
  toTier: string;
  /** Optional — subject id 用於顯示中文標籤;若 absent 顯示 generic '段位'。 */
  subject?: Subject;
}

interface ResultData {
  outcome: 'victory' | 'defeat';
  /**
   * 雙軌進度(spec §3.4):本場「指揮官經驗」基準值。歷史欄位名沿用 `freeXP`
   * 以免大改 caller,但語意已改為玩遊戲軌的指揮官經驗(NOT 學習軌 freeXP)。
   * 當 caller 帶 `battleStats` 時,result-scene 以 computeCommanderXp 重算為準,
   * 此值僅作 fallback。
   */
  freeXP: number;
  bossDefeated?: boolean;
  bossName?: string;
  /** 穩定 boss id(對齊 boss-pool 各檔 def.id)。供「打贏魔王首殺解鎖武器類型 +
   *  魔王圖鑑」查 boss-weapon-unlocks。undefined / 'tutorial-boss' / 未知 → 不解鎖。 */
  bossId?: string;
  battleStats?: { wave: number; killed: number; survivedSec: number; score?: number };
  /** Optional XP breakdown for tap-to-expand display (parent transparency). */
  xpBreakdown?: XPBreakdownEntry[];
  /**
   * Snapshot of skillLevels captured at battle start. ResultScene diffs this
   * vs progress.skillLevels (post-battle) to fire ⚡ Lv-up floats. If absent,
   * scene falls back to localStorage snapshot from loadLastSkillLevelsSnapshot.
   * Both absent → no level-up celebration (silent no-op).
   */
  prevSkillLevels?: Record<string, number>;
  /**
   * W8 PR-3 — 前一次戰鬥 score(供「比上次高 N 分」UI 顯示;只在 victory 且
   * battleStats.score > prevScore 時提示)。
   */
  prevScore?: number;
  /**
   * W8 PR-3 — Mastery box 升級陣列。caller(battle scene 或 mastery layer)
   * 已 diff 完傳入,ResultScene 只負責 UI 動畫,不再讀 question API。
   * 為空或 undefined → 不顯示升級動畫(靜默)。
   */
  upgradedBoxes?: MasteryBoxUpgrade[];
  /**
   * W8 PR-3 — 段位變化。為 single object(spec §PR-3 task)— 對應「本關
   * 主要科目段位變化」,如為 undefined 則不顯示。subject 為 optional,
   * 用於 label 中文化顯示。
   */
  upgradedLevels?: SubjectLevelUpgrade;
  /**
   * W8 PR-3 — Hub 對應 factionId,供「下一關」CTA 與「比上次高 N 分」
   * progress record 索引使用。為 undefined → 不顯示「比上次高」對比。
   */
  hubFactionId?: FactionId;
  /**
   * 關卡遞進:本關 stage 編號(battle scene 傳入,預設 1)。「下一關」按鈕
   * 以 stage+1 重開 HorizontalBattleScene,battle scene 的 stageMul 吃 stage
   * 把難度往上加。為 undefined → 視為第 1 關。
   */
  stage?: number;
  /**
   * 雙軌進度(spec §3.2.1):本場是否通關(清完所有波次/該 stage)。
   * 計入指揮官經驗公式 (+50)。預設 undefined → false。
   */
  stageCleared?: boolean;
  /**
   * 闖關評星用:勝利當下玩家剩餘血量比例(0..1)。battle scene 在 _bossDefeated
   * 帶入。result-scene 用 computeStars 評星(>=0.8→3★ / >=0.4→2★ / 其餘→1★)。
   * 純表現度量,不影響任何戰力。undefined → 視為 0(最低 1★,因為有通關)。
   */
  hpRatioAtWin?: number;
}

export class ResultScene extends Phaser.Scene {
  constructor() { super('ResultScene'); }

  /** Generate a default XP breakdown when the caller doesn't supply one. */
  _defaultBreakdown(freeXP: number, isBoss: boolean): XPBreakdownEntry[] {
    if (freeXP <= 0) return [];
    if (isBoss) {
      const baseWave = Math.round(freeXP * 0.5);
      const bossBonus = freeXP - baseWave;
      return [
        { label: '波次完成獎勵', amount: baseWave },
        { label: '魔王擊敗加成', amount: bossBonus },
      ];
    }
    const baseWave = Math.round(freeXP * 0.7);
    const killBonus = freeXP - baseWave;
    return [
      { label: '波次完成獎勵', amount: baseWave },
      { label: '擊殺獎勵', amount: killBonus },
    ];
  }

  /**
   * Add a tap/click toggle that shows an XP breakdown panel below the given Phaser text object.
   * Uses a DOM overlay so it works on touch devices.
   */
  _addXPBreakdownToggle(
    textObj: Phaser.GameObjects.Text,
    breakdown: XPBreakdownEntry[],
    centerX: number,
    panelY: number,
  ): void {
    if (!breakdown || breakdown.length === 0) return;

    let expanded = false;
    let panel: HTMLDivElement | null = null;

    const canvasParent = this.game?.canvas?.parentElement;

    const removePanel = () => {
      if (panel) { panel.remove(); panel = null; }
    };

    textObj.on('pointerdown', () => {
      expanded = !expanded;
      if (!expanded) {
        removePanel();
        return;
      }
      if (!canvasParent) return;

      panel = document.createElement('div');
      panel.className = 'xp-breakdown-panel';
      panel.id = 'xp-breakdown-panel';

      const canvasRect = this.game.canvas.getBoundingClientRect();
      const scaleX = canvasRect.width / GAME.width;
      const scaleY = canvasRect.height / GAME.height;

      panel.style.cssText = `
        position: absolute;
        left: ${canvasRect.left + (centerX - 120) * scaleX}px;
        top:  ${canvasRect.top  + panelY * scaleY}px;
        width: ${240 * scaleX}px;
        background: rgba(10,15,30,0.95);
        border: 1px solid #ffd54f;
        border-radius: 6px;
        padding: 10px 14px;
        z-index: 9999;
        font-family: sans-serif;
        color: #ffd54f;
        font-size: ${14 * scaleY}px;
        pointer-events: none;
      `;

      const rows = breakdown.map(e =>
        `<div style="display:flex;justify-content:space-between;margin-bottom:4px;"><span style="color:#ccc">${e.label}</span><span>+${e.amount}</span></div>`
      ).join('');
      const link = `<div style="margin-top:6px;border-top:1px solid #37474f;padding-top:6px;font-size:${11 * scaleY}px;color:#80cbc4;">指揮官經驗來自戰鬥表現，升級解鎖英雄、造型、關卡與模式！</div>`;
      panel.innerHTML = rows + link;

      document.body.appendChild(panel);
    });

    // Clean up panel when scene stops
    this.events.once('shutdown', removePanel);
    this.events.once('destroy', removePanel);
  }

  /**
   * VICTORY-CELEBRATION (task #34): fire confetti + emblem reveal + level-up
   * floats on any victory path (normal or boss). Boss-victory bumps confetti
   * to 80 particles for extra punch. All calls are try-guarded so the test
   * harness (which has minimal Phaser stubs) can still exercise the scene.
   *
   * @param progressSkillLevels — post-battle skillLevels from loaded progress.
   * @param prevSnapshotFromCaller — explicit prev snapshot if caller provided;
   *   otherwise we fall back to localStorage.
   */
  _fireVictoryFx(
    isBoss: boolean,
    progressSkillLevels: Record<string, number> | null,
    prevSnapshotFromCaller: Record<string, number> | undefined,
  ): void {
    const cx = GAME.width / 2;
    const cy = GAME.height / 2;

    // 1. Confetti — 80 for boss-victory, 60 default
    try {
      spawnConfetti(this, cx, cy, { count: isBoss ? 80 : 60 });
    } catch { /* harness */ }

    // 2. Faction emblem reveal → 收成「頂部小徽章」(boss 2026-06-14 重設計)。
    // 原本 reveal 大徽章後淡成 alpha 0.26 浮水印,正好疊在大標/boss 名後面把字糊掉
    // (老闆截圖主因)。改法:reveal 跑完(700ms)後把徽章「縮小 + 上移」到標題帶
    // 上方當一枚清楚小徽章(_settleEmblemCrest),不再當蓋字浮水印。
    // revealEmblem 仍在中央偏上 reveal(慶祝動畫),settle 再收到頂部 crest 槽。
    try {
      const progress = loadProgress();
      const faction = pickDisplayFaction(progress?.factionXP ?? null);
      const emblemKey = emblemTextureKey(faction);
      revealEmblem(this, faction, cx, cy - 180);
      try {
        this.time.delayedCall(700, () => { this._settleEmblemCrest(emblemKey, cx, 40); });
      } catch { /* headless — no time system */ }
    } catch { /* harness — pickDisplayFaction needs full progress */ }

    // 3. Level-up floats — diff prev vs post snapshot
    try {
      const prev = prevSnapshotFromCaller ?? loadLastSkillLevelsSnapshot();
      const diffs = diffSkillLevels(prev, progressSkillLevels);
      if (diffs.length > 0) {
        // 升級 float 從中段升起並淡出(2.2s),別卡在底部 CTA 上(原 cy+180=480 會
        // 跟「回 hub」按鈕重疊)。改成 cy+30,飄過中段後消失,不擋按鈕。
        showLevelUpFloats(this, diffs, cx, cy + 30);
        // Unlock-fanfare SFX hook (SoundPlayer mock-friendly). If 'unlock' key
        // missing in current SFX library, SoundPlayer.play throws silently.
        try {
          const sfx = new SoundPlayer();
          sfx.play('unlock-fanfare', 0.7);
        } catch { /* AudioContext busy or SFX key missing */ }
      }
    } catch { /* harness */ }
  }

  /**
   * W8 PR-3 — Mastery box 升級動畫。
   *
   * 觸發條件:`data.upgradedBoxes` 非空。每筆顯示一個彩光圓圈 +
   * 「{kpId} box {from}→{to}」字串,scale 1→1.3 yoyo punch tween,
   * 再 alpha fade out。垂直堆疊間距 36px。
   *
   * 顏色:用 caller 提供的 `hubFactionId` 對應 `FACTION_BURST_COLOR`;
   * 若無 hubFactionId,fallback 通用金。spec §4.4 強調 result-scene
   * 不參與戰鬥計算,只負責顯示 caller 已 diff 完的結果。
   *
   * 全 try-guarded:headless 測試環境(無真 Phaser display list)不會
   * crash;tween 失敗時 onComplete 內 destroy 也用 try/catch。
   */
  _animateMasteryBoxUpgrade(
    upgradedBoxes: MasteryBoxUpgrade[],
    centerX: number,
    startY: number,
    factionId?: FactionId,
  ): void {
    if (!upgradedBoxes || upgradedBoxes.length === 0) return;
    const color = (factionId != null && FACTION_BURST_COLOR[factionId] != null)
      ? FACTION_BURST_COLOR[factionId]!
      : FACTION_BURST_FALLBACK_COLOR;
    upgradedBoxes.forEach((upgrade, i) => {
      const y = startY + i * 36;
      try {
        const circle = this.add.graphics();
        circle.fillStyle(color, 0.9);
        circle.fillCircle(0, 0, 14);
        circle.setPosition(centerX - 110, y);
        circle.setDepth(15);
        this.tweens.add({
          targets: circle,
          scaleX: 1.3, scaleY: 1.3,
          duration: 200, yoyo: true, ease: 'Quad.easeOut',
          onComplete: () => {
            try {
              this.tweens.add({
                targets: circle, alpha: 0, duration: 1200,
                onComplete: () => { try { circle.destroy(); } catch { /* headless */ } },
              });
            } catch { /* headless */ }
          },
        });
      } catch { /* headless — graphics or tween unavailable */ }

      try {
        const hex = '#' + color.toString(16).padStart(6, '0');
        const label = this.add.text(centerX - 80, y - 10,
          `${upgrade.kpId} box ${upgrade.fromBox}→${upgrade.toBox}`,
          { fontSize: '16px', color: hex, fontFamily: 'sans-serif' },
        );
        try {
          this.tweens.add({
            targets: label, alpha: 0, delay: 1200, duration: 600,
            onComplete: () => { try { label.destroy(); } catch { /* headless */ } },
          });
        } catch { /* headless */ }
      } catch { /* headless — text unavailable */ }
    });
  }

  /**
   * W8 PR-3 — 「比上次高 N 分」UI。`hubFactionId` 有值且 prevScore 與
   * 當次 score 都 ≥0 且差距正數時顯示。spec §4.4 要求綠色字。
   */
  _showScoreDelta(
    currentScore: number,
    prevScore: number,
    centerX: number,
    y: number,
  ): void {
    const delta = currentScore - prevScore;
    if (delta <= 0) return;
    try {
      const t = this.add.text(centerX, y, `比上次高 ${delta} 分!`, {
        fontSize: '18px', color: '#69f0ae', fontFamily: 'sans-serif',
      });
      try { t.setOrigin?.(0.5); } catch { /* headless */ }
    } catch { /* headless */ }
  }

  /**
   * GAME_OVERHAUL_PLAN W2-3「結算畫面儀式化」— 星星逐顆登場。
   *
   * 永遠畫滿 3 個星位(置中於 centerX,間距 STAR_GAP);實心 ★(已得)以
   * scale 0 → 1.0「啪」跳出(yoyo punch tween)+ 一聲 xp-popup;空心 ☆(未得)
   * 同步鋪底、固定大小、不 pop。每顆延遲 STAR_DELAY 依序登場,給拿星儀式感。
   *
   * K-3 友善、克制:只縮放跳一下(scale punch),不閃光、不過度。純展示,
   * 不碰任何 progress / 評星計算(filled 由 caller 算好傳入)。全 try-guarded:
   * headless 測試環境(無 tweens / SoundPlayer)直接以最終大小落定,不 crash。
   *
   * @param filled  已得星數(0..3)。
   * @param centerX 星列中心 x。
   * @param y       星列 y(與「關卡通關!」同列)。
   */
  _popCampaignStars(filled: number, centerX: number, y: number): void {
    const f = Math.max(0, Math.min(3, Math.round(filled)));
    const STAR_GAP = 34;     // 星與星水平間距
    const STAR_DELAY = 260;  // 每顆登場間隔(ms)
    // 三顆星水平置中:位置 = centerX + (i-1)*GAP(i=0..2)。
    let sfx: SoundPlayer | null = null;
    try { sfx = new SoundPlayer(); } catch { sfx = null; }

    for (let i = 0; i < 3; i++) {
      const isFilled = i < f;
      const glyph = isFilled ? '★' : '☆';
      const color = isFilled ? '#ffd54f' : '#5b6472';
      const sx = centerX + (i - 1) * STAR_GAP - 60; // -60:讓星列偏左,右側留給「關卡通關!」
      try {
        const star = this.add.text(sx, y, glyph, {
          fontSize: '30px', color, fontFamily: 'sans-serif', fontStyle: 'bold',
        });
        try { star.setOrigin?.(0.5); } catch { /* headless */ }
        try { star.setDepth?.(21); } catch { /* headless */ }

        if (isFilled) {
          // pop:從小放大「啪」一下。先設 scale 0,delay 後 tween 到 1 再微 yoyo。
          try { star.setScale?.(0.1); } catch { /* headless */ }
          const delay = i * STAR_DELAY;
          try {
            this.tweens.add({
              targets: star,
              scaleX: 1, scaleY: 1,
              ease: 'Back.easeOut',
              delay,
              duration: 220,
              onStart: () => { try { sfx?.play('xp-popup', 0.5); } catch { /* AudioContext busy */ } },
            });
          } catch {
            // headless / no tween — 直接落定大小,並補一聲(若 sfx 可用)。
            try { star.setScale?.(1); } catch { /* noop */ }
            try { sfx?.play('xp-popup', 0.5); } catch { /* noop */ }
          }
        }
      } catch { /* headless — add.text unavailable */ }
    }
  }

  /**
   * 闖關過關提示 — 顯示星等(★★☆ 形式)+(boss 關)配件解鎖一行。
   * 純展示,全 try-guarded(headless)。star 上限 3。
   *
   * 鐵則:這裡只「顯示」markStageCleared 已寫好的結果,result-scene 不重複
   * 寫任何 progress 欄位。
   */
  _showCampaignClear(
    result: StageClearResult,
    centerX: number,
    y: number,
    hpRatio?: number,
  ): void {
    const filled = Math.max(0, Math.min(3, Math.round(result.stars)));

    // 儀式化(GAME_OVERHAUL_PLAN W2-3):星星「逐顆登場」。把 ★★★ 三顆各自畫成
    // 獨立 text,從 scale 0 → 1.0「啪」跳出(yoyo punch),每顆延遲 ~260ms,再
    // 配一聲 xp-popup(bling),營造拿星儀式感。空星(☆)同時鋪底但不 pop。
    // K-3 友善、克制:只縮放跳一下,不閃光不過度。全 try-guarded(headless 安全)。
    this._popCampaignStars(filled, centerX, y);

    // 「關卡通關!」標題:緊接星星,顯示在星列右側(星列佔約 4 字寬)。
    try {
      const t = this.add.text(centerX + 60, y, '關卡通關!', {
        fontSize: '24px', color: '#ffe082', fontFamily: 'sans-serif', fontStyle: 'bold',
      });
      try { t.setOrigin?.(0, 0.5); } catch { /* headless */ }
      try { t.setDepth?.(21); } catch { /* headless */ }
    } catch { /* headless */ }

    // 評星規則可見化(boss 2026-06-13「怎樣才算三顆星」):顯示本場剩餘血量 +
    // 升星門檻,讓玩家知道「怎麼拿到下一顆星」。純展示,不影響評星計算。
    if (typeof hpRatio === 'number' && Number.isFinite(hpRatio)) {
      const pct = Math.round(Math.max(0, Math.min(1, hpRatio)) * 100);
      const ruleMsg = filled >= 3
        ? `剩餘血量 ${pct}% — 無傷過關,滿星!`
        : `剩餘血量 ${pct}%　(血量 ≥80% 得 ★★★、≥40% 得 ★★)`;
      try {
        const tr = this.add.text(centerX, y + 26, ruleMsg, {
          fontSize: '14px', color: '#cfd8e8', fontFamily: 'sans-serif',
        });
        try { tr.setOrigin?.(0.5); } catch { /* headless */ }
        try { tr.setDepth?.(21); } catch { /* headless */ }
      } catch { /* headless */ }
    }

    // 配件解鎖一行(boss 關才會有)。中文標籤對映;未知 id 直接顯示 id。
    // boss 2026-06-13「取得多少寶物(永久?)」:明寫「永久」,跟「金幣/場內升級
    // = 本場用完歸零」區隔(鐵則:配件解鎖屬帳號廣度,永久保留)。
    if (result.unlockedAccessories.length > 0) {
      const ACC_LABEL: Record<string, string> = { drone: '僚機', scythe: '機械斧' };
      const names = result.unlockedAccessories.map((a) => ACC_LABEL[a] ?? a).join('、');
      try {
        const t2 = this.add.text(centerX, y + 48, `🎁 永久解鎖配件:${names}`, {
          fontSize: '18px', color: '#80cbc4', fontFamily: 'sans-serif',
        });
        try { t2.setOrigin?.(0.5); } catch { /* headless */ }
        try { t2.setDepth?.(21); } catch { /* headless */ }
      } catch { /* headless */ }
    }
  }

  /**
   * Juice Batch 1 — 數字累加動畫。把一個 Phaser Text 從 0「tick 累加」到
   * `target`(預設 ~0.8s,ease-out),每跳一格(顯示值改變)播一聲輕「嗒」。
   *
   * 純呈現:不碰任何 progress / 修煉值資料(target 由 caller 算好傳入)。
   * 全 try-guarded — headless 測試環境(無 time.addEvent / SoundPlayer)直接
   * 把文字設成最終值,不會 crash。
   *
   * @param textObj  目標文字物件(create 時先以 prefix+0+suffix 顯示)。
   * @param target   最終整數值。
   * @param prefix   數字前綴(如 '消滅 ')。
   * @param suffix   數字後綴(如 ' 隻')。
   * @param durationMs 動畫長度(預設 800)。
   * @param onComplete 動畫結束 callback(供「修煉值飛入進度條」串接)。
   */
  _animateCountUp(
    textObj: Phaser.GameObjects.Text,
    target: number,
    prefix = '',
    suffix = '',
    durationMs = 800,
    onComplete?: () => void,
  ): void {
    const setText = (v: number) => {
      try { textObj.setText(`${prefix}${v}${suffix}`); } catch { /* headless */ }
    };
    // target 0 或負 → 沒東西可累加,直接落定。
    if (!(target > 0)) {
      setText(Math.round(target));
      try { onComplete?.(); } catch { /* noop */ }
      return;
    }

    let lastShown = 0;
    setText(0);

    // 每跳一格的輕「嗒」音 — 複用 xp-popup(高頻短 sparkle)。共用一個
    // SoundPlayer instance 避免每跳 new AudioContext。
    let sfx: SoundPlayer | null = null;
    try { sfx = new SoundPlayer(); } catch { sfx = null; }
    const tick = () => {
      try { sfx?.play('ui-click', 0.18); } catch { /* AudioContext busy */ }
    };

    // start 在「第一次 step()」才捕捉(lazy),而非 create 當下。原因:scene 剛
    // mount 時 time.now 可能有大跳躍(stopped scene 的 clock 在 resume 首幀補時),
    // 若在 create 捕 start,首次 step 的 elapsed 會瞬間 >> duration → 動畫直接跳完。
    // 改成首幀才定 start,確保 elapsed 從 0 累積。
    let start: number | null = null;
    let event: Phaser.Time.TimerEvent | null = null;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      setText(target);
      try { event?.remove(false); } catch { /* noop */ }
      try { onComplete?.(); } catch { /* noop */ }
    };

    const step = () => {
      const now = (typeof this.time?.now === 'number') ? this.time.now : (start ?? 0) + durationMs;
      if (start === null) start = now; // lazy start on first tick
      const elapsed = now - start;
      const v = tickValue(elapsed, durationMs, target);
      if (v !== lastShown) {
        lastShown = v;
        setText(v);
        tick();
      }
      if (elapsed >= durationMs) finish();
    };

    try {
      // ~30fps update — frequent enough to feel smooth, sparse enough that the
      // 「嗒」聲不會太密集。
      event = this.time.addEvent({ delay: 33, loop: true, callback: step });
    } catch {
      // headless / no time system — snap to final value immediately.
      finish();
    }
  }

  /**
   * Juice Batch 1 — 「修煉值飛入進度條」收尾。累加結束後,從 XP 文字位置
   * 朝畫面上方(門派徽章 / 進度條方向)飛出數枚發光小方塊並收束,給「存進去」
   * 的兌現感 + 一聲 xp-popup。
   *
   * 純呈現、全 try-guarded。amount<=0 直接 no-op。
   */
  _flyXPToGauge(fromX: number, fromY: number, amount: number): void {
    if (!(amount > 0)) return;
    const targetX = GAME.width / 2;
    const targetY = 40; // 畫面頂端(徽章/進度條區)
    const COUNT = Math.min(8, Math.max(3, Math.round(amount / 20)));
    try {
      const sfx = new SoundPlayer();
      sfx.play('xp-popup', 0.5);
    } catch { /* AudioContext busy */ }
    for (let i = 0; i < COUNT; i++) {
      try {
        const p = this.add.rectangle(
          fromX + (Math.random() - 0.5) * 40,
          fromY + (Math.random() - 0.5) * 20,
          8, 8, 0xffd54f, 1,
        ).setDepth(60);
        this.tweens.add({
          targets: p,
          x: targetX + (Math.random() - 0.5) * 30,
          y: targetY,
          alpha: 0,
          scaleX: 0.3, scaleY: 0.3,
          duration: 420 + Math.random() * 180,
          delay: i * 30,
          ease: 'Cubic.easeIn',
          onComplete: () => { try { p.destroy(); } catch { /* noop */ } },
        });
      } catch { /* headless — add.rectangle / tweens unavailable */ }
    }
  }

  /**
   * GAME_OVERHAUL_PLAN W2-3「結算畫面儀式化」— 單列「飛入登場」。
   *
   * 把一個已建立的 text object 以「淡入 + 由上方輕落」進場,延遲 `delayMs` 後
   * 啟動,讓各區塊有節奏地依序冒出(標題→boss名→星星→消滅→經驗),而不是
   * 一次全冒。純表現、全 try-guarded:headless(無 tweens)直接設為可見最終態,
   * 不 crash、不影響文字內容(測試斷言看的是 text 內容,不受影響)。
   *
   * @param obj     目標 text/物件(需支援 setAlpha / y 屬性)。
   * @param finalY  落定後的 y(進場前從 finalY - rise 落下)。
   * @param delayMs 進場延遲(串接成序列)。
   * @param rise    起始上移量(px,預設 16)。
   */
  _revealRow(
    obj: Phaser.GameObjects.Text,
    finalY: number,
    delayMs: number,
    rise = 16,
  ): void {
    try {
      // 起始:透明 + 略高一點。
      try { obj.setAlpha?.(0); } catch { /* headless */ }
      try { (obj as { y?: number }).y = finalY - rise; } catch { /* headless */ }
      this.tweens.add({
        targets: obj,
        alpha: 1,
        y: finalY,
        ease: 'Quad.easeOut',
        delay: delayMs,
        duration: 260,
      });
    } catch {
      // headless / no tween — 直接落定可見最終態。
      try { obj.setAlpha?.(1); } catch { /* noop */ }
      try { (obj as { y?: number }).y = finalY; } catch { /* noop */ }
    }
  }

  /**
   * 結算重設計(boss 2026-06-14)— 把 reveal 完成的門派徽章「收成頂部小徽章」。
   *
   * 原 _dimEmblem 把 410px 徽章淡成 alpha 0.26 當「背景浮水印」,但它正好疊在
   * 大標/boss 名後面 → 字糊成一團(老闆截圖主因之一)。改法:reveal 動畫跑完後
   * 把徽章「縮小 + 上移」到標題帶上方當一枚清楚的小徽章(不再是蓋字的浮水印),
   * alpha 微透留慶祝氛圍但不干擾文字。掃 display list 找 image(lazy-load 安全)。
   * 全 try-guarded(headless 安全)。
   */
  _settleEmblemCrest(textureKey: string, crestX: number, crestY: number): void {
    try {
      const list = (this.children as unknown as { list?: unknown[] })?.list;
      if (!Array.isArray(list)) return;
      for (const obj of list) {
        const img = obj as { texture?: { key?: string }; type?: string };
        if (img?.type === 'Image' && img.texture?.key === textureKey) {
          const target = obj as Phaser.GameObjects.Image;
          try { target.setDepth?.(2); } catch { /* headless */ }
          try {
            this.tweens.add({
              targets: target,
              x: crestX, y: crestY,
              scale: 0.34, alpha: 0.5,
              duration: 420, ease: 'Quad.easeOut',
            });
          } catch {
            try { target.setPosition?.(crestX, crestY); } catch { /* noop */ }
            try { target.setScale?.(0.34); } catch { /* noop */ }
            try { target.setAlpha?.(0.5); } catch { /* noop */ }
          }
        }
      }
    } catch { /* headless — no children list */ }
  }

  /**
   * 結算重設計 — 一張「獎勵列表項目」(圖示 + 文字,左對齊一行)。
   * 動態列表共用:解鎖武器 / 主武器 / 圖鑑進度 / 永久配件 / 段位變化 / 指揮官升等。
   * 每項一行、左圖右文,不置中堆疊 → 不再互相蓋。delayMs 串成依序冒出。
   *
   * @param leftX  項目卡左緣 x。
   * @param y      項目中心 y。
   * @param w      卡寬。
   * @param icon   前綴圖示(emoji)。
   * @param text   主文字。
   * @param color  文字色(hex 字串)。
   * @param delayMs 進場延遲。
   */
  _addRewardItem(
    leftX: number, y: number, w: number,
    icon: string, text: string, color: string, delayMs: number,
    accentColor: number,
  ): void {
    const rowH = 34;
    try {
      // 卡底:深色圓角帶 + 門派色左邊條,讓每項有「卡片感」、彼此分隔清楚。
      const card = this.add.rectangle(leftX + w / 2, y, w, rowH, 0x10172e, 0.82)
        .setStrokeStyle(1.5, accentColor, 0.55)
        .setDepth(18);
      const bar = this.add.rectangle(leftX + 4, y, 5, rowH - 8, accentColor, 1).setDepth(19);
      const iconText = this.add.text(leftX + 18, y, icon, {
        fontSize: '18px', fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5).setDepth(19);
      const label = this.add.text(leftX + 44, y, text, {
        fontSize: '17px', color, fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0, 0.5).setDepth(19);
      // 依序冒出(淡入 + 微右滑)。
      for (const o of [card, bar, iconText, label]) {
        try { (o as { setAlpha?: (a: number) => void }).setAlpha?.(0); } catch { /* noop */ }
      }
      try {
        this.tweens.add({
          targets: [card, bar, iconText, label],
          alpha: 1, duration: 240, delay: delayMs, ease: 'Quad.easeOut',
        });
      } catch {
        for (const o of [card, bar, iconText, label]) {
          try { (o as { setAlpha?: (a: number) => void }).setAlpha?.(1); } catch { /* noop */ }
        }
      }
    } catch { /* headless — add.rectangle/text unavailable */ }
  }

  /**
   * 結算重設計 — 指揮官 XP 進度條。顯示「Lv N(升等轉金)」+ 下一級提示 + 底槽 +
   * 依累計進度填充。升等時整條高亮 pulse。純展示(資料由 caller 算好)。
   * 本場 +gained 由 caller 的 count-up 小標呈現,不在此重複。
   *
   * @param cx       畫面中心 x。
   * @param y        條中心 y。
   * @param totalXp  目前累計 commanderXp(填充比例用)。
   * @param level    目前等級。
   * @param leveledUp 是否升等(高亮)。
   * @param accent   門派色。
   */
  _renderCommanderXpBar(
    cx: number, y: number, totalXp: number, level: number,
    leveledUp: boolean, accent: number,
  ): void {
    const barW = 300;
    const barH = 18;
    const leftX = cx - barW / 2;
    // 本級區間填充比例(threshold 表)。封頂或無下一級 → 滿。
    const lvIdx = Math.max(0, Math.min(level - 1, COMMANDER_LEVEL_THRESHOLDS.length - 1));
    const lvFloor = COMMANDER_LEVEL_THRESHOLDS[lvIdx] ?? 0;
    const toNext = commanderXpToNextLevel(totalXp);
    const span = toNext > 0 ? (totalXp - lvFloor) + toNext : 1;
    const ratio = toNext > 0 ? Math.max(0.04, Math.min(1, (totalXp - lvFloor) / Math.max(1, span))) : 1;
    try {
      // Lv 標籤(左,升等轉金 + 加「升級!」徽記)。
      const lvLabel = leveledUp ? `指揮官 Lv ${level}　升級!` : `指揮官 Lv ${level}`;
      this.add.text(leftX, y - 18, lvLabel, {
        fontSize: '16px', color: leveledUp ? '#ffe082' : '#9fb3d8',
        fontFamily: 'sans-serif', fontStyle: leveledUp ? 'bold' : 'normal',
      }).setOrigin(0, 0.5).setDepth(19);
      // 下一級提示(右):還差多少 / 已封頂。
      const nextHint = toNext > 0 ? `下一級還差 ${toNext}` : '已封頂';
      this.add.text(leftX + barW, y - 18, nextHint, {
        fontSize: '13px', color: '#6b7ea8', fontFamily: 'sans-serif',
      }).setOrigin(1, 0.5).setDepth(19);
      // 底槽。
      this.add.rectangle(cx, y, barW, barH, 0x0b1024, 0.95)
        .setStrokeStyle(2, accent, 0.8).setDepth(18);
      // 填充(從 0 漲到 ratio)。
      const fill = this.add.rectangle(leftX + 2, y, 1, barH - 6, accent, 1)
        .setOrigin(0, 0.5).setDepth(19);
      const finalW = Math.max(2, (barW - 4) * ratio);
      try {
        this.tweens.add({
          targets: fill, width: finalW, duration: 650, delay: 200, ease: 'Cubic.easeOut',
        });
      } catch { try { (fill as { width?: number }).width = finalW; } catch { /* noop */ } }
      if (leveledUp) {
        // 升等 pulse:整條 stroke 閃一下(克制,不狂閃)。
        try {
          this.tweens.add({
            targets: fill, alpha: 0.55, yoyo: true, repeat: 2,
            duration: 200, delay: 850,
          });
        } catch { /* headless */ }
      }
    } catch { /* headless */ }
  }

  /**
   * 結算重設計 — 統計格(波次 / 消滅 / 得分),三欄等寬、數字大而清楚。
   * 取代原本疊在一起、太淡、被切的小字。純展示。
   *
   * @param cx     中心 x。
   * @param y      格中心 y。
   * @param stats  本場數據。
   */
  _renderStatsGrid(
    cx: number, y: number,
    stats: { wave: number; killed: number; score?: number },
  ): void {
    const cells: Array<{ label: string; value: string }> = [
      { label: '波次', value: `${stats.wave}` },
      { label: '消滅', value: `${stats.killed}` },
      { label: '得分', value: `${stats.score ?? 0}` },
    ];
    const totalW = 300;
    const cellW = totalW / cells.length;
    const leftX = cx - totalW / 2;
    try {
      // 背板。
      this.add.rectangle(cx, y, totalW + 16, 60, 0x10172e, 0.7)
        .setStrokeStyle(1.5, 0x2b3a63, 1).setDepth(18);
      cells.forEach((c, i) => {
        const colX = leftX + cellW * i + cellW / 2;
        // 數值(大、亮)。
        this.add.text(colX, y - 8, c.value, {
          fontSize: '26px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(19);
        // 標籤(小、灰)。
        this.add.text(colX, y + 18, c.label, {
          fontSize: '14px', color: '#8aa0c8', fontFamily: 'sans-serif',
        }).setOrigin(0.5).setDepth(19);
        // 分隔線。
        if (i > 0) {
          this.add.rectangle(leftX + cellW * i, y, 1, 40, 0x2b3a63, 1).setDepth(19);
        }
      });
    } catch { /* headless */ }
  }

  /**
   * boss 2026-06-16:關卡結束顯示該門派英雄的勝利(雀躍)/ 失敗(受傷跪地)姿勢。
   * 每門派各 4 勝利 + 2 失敗,隨機挑一張;runtime 載入(只載要用的那張),缺圖靜默不顯示。
   * 放畫面左側、低 depth(當背景襯托,不擋中央 UI);勝利圖加輕微上下跳呼應雀躍。
   */
  private _showHeroPose(faction: FactionId, outcome: 'victory' | 'defeat'): void {
    try {
      const count = outcome === 'victory' ? 3 : 4;
      const pick = 1 + Math.floor(Math.random() * count);
      const key = `hero-pose-${faction}-${outcome}-${pick}`;
      const url = `assets/ai-gen/heroes/poses/${faction}-${outcome}-${pick}.png`;
      const place = (): void => {
        try {
          if (!this.textures.exists(key)) return;
          const src = this.textures.get(key).getSourceImage() as { height?: number };
          const ih = src.height || 1152;
          const scale = (GAME.height * 0.84) / ih;
          const img = this.add.image(GAME.width * 0.17, GAME.height * 0.56, key).setScale(scale).setDepth(1).setAlpha(0);
          this.tweens.add({ targets: img, alpha: 1, duration: 420, ease: 'Quad.easeOut' });
          if (outcome === 'victory') {
            this.tweens.add({ targets: img, y: img.y - 14, duration: 720, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
          }
        } catch { /* headless */ }
      };
      if (this.textures.exists(key)) { place(); return; }
      this.load.image(key, url);
      this.load.once('complete', place);
      this.load.once('loaderror', () => { /* 缺圖 → 不顯示 */ });
      this.load.start();
    } catch { /* headless */ }
  }

  create(data: ResultData): void {
    const {
      outcome, freeXP, bossDefeated, bossName, bossId, battleStats, xpBreakdown, prevSkillLevels,
      prevScore, upgradedBoxes, upgradedLevels, hubFactionId, stage, stageCleared = false,
      hpRatioAtWin,
    } = data;

    // Play victory/defeat sound on scene creation. Asset Pipeline B: the
    // 'victory' kind maps to the CC0 victory-sting.ogg. Sample decode is async,
    // so we play once samples settle (sting on victory); if loading is slow or
    // fetch is unavailable (tests), play() falls back to the synth fanfare.
    try {
      const sfx = new SoundPlayer();
      const kind = outcome === 'victory' ? 'victory' : 'defeat';
      const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '';
      const result = sfx.loadSamples(base);
      if (result && typeof result.then === 'function') {
        result.then(() => { try { sfx.play(kind, 0.5); } catch { /* AudioContext busy */ } });
      } else {
        sfx.play(kind, 0.5);
      }
    } catch {
      // AudioContext may be unavailable in test environments — ignore
    }

    // boss 2026-06-16「每個角色畫勝利/失敗姿勢、用在關卡結束」:依玩家門派 + 勝敗,
    // 顯示一張隨機英雄姿勢(勝=雀躍歡呼、敗=受傷跪地)。純演出,缺圖 no-op。
    try {
      const prog = loadProgress();
      // 用「玩家選的英雄門派」決定姿勢(玩紫苑就看到紫苑);沒選過退回最高 XP 門派。
      const poseFaction = ((prog?.selectedHeroId as FactionId | undefined)
        ?? pickDisplayFaction(prog?.factionXP ?? null));
      this._showHeroPose(poseFaction, outcome);
    } catch { /* headless / no progress */ }

    // 雙軌進度(spec §3.4):戰鬥勝利只發「指揮官經驗」(玩遊戲軌),
    // **不再** addFreeXP(切斷「玩遊戲→freeXP→可被 allocateFreeXP 灌進
    // factionXP」這條,鐵則 §7)。commanderXp 由戰鬥表現算 — 公式 §3.2.1。
    let postSkillLevels: Record<string, number> | null = null;
    let commanderGained = 0;
    let commanderResult: ReturnType<typeof addCommanderXp> | null = null;
    // R-005 P2:本場發放的武器升級素材(玩遊戲軌)。0 戰力寫入。
    let shardsGained = 0;
    // 永久 meta 商店(PowerUp / meta-shop)金幣 — 玩遊戲軌,比照 commanderXp 寫法。
    // 鐵則(LONG_TERM_PROGRESSION §6):meta 金幣絕不等同/換算 factionXP/freeXP/skillLevels/修煉值。
    let metaCoinsGained = 0;
    if (outcome === 'victory') {
      // 計算這場該發多少指揮官經驗。優先用 battleStats(wave/killed)+ boss/通關;
      // 若 caller 沒帶 battleStats,退回 payload 的數字欄位(舊 freeXP 欄位語意
      // 改作「本場指揮官經驗」)當作 fallback,確保不退步。
      const formula = computeCommanderXp(battleStats, !!bossDefeated, stageCleared);
      const baseGained = formula > 0 ? formula : (freeXP > 0 ? freeXP : 0);
      // R-004 D5:通關獎勵(指揮官 XP)隨章節遞增 — 後面的章節打贏給更多廣度獎勵。
      //   倍率由 stage 推回章節配方(rewardMultiplierForStage),資料驅動;
      //   只放大「玩遊戲軌」commanderXp,**不**碰 factionXP/skillLevels(鐵則)。
      const rewardMult = rewardMultiplierForStage(stage ?? 1);
      commanderGained = Math.round(baseGained * rewardMult);
      // R-005 P2:武器升級素材(weaponShards)— 玩遊戲軌,比照 commanderXp 寫法。
      //   公式:波數 + 魔王(+5)+ 通關(+3),任何勝利至少 1。鐵則:純武器養成貨幣,
      //   **絕不**等同/換算 factionXP/freeXP/skillLevels/修煉值。
      shardsGained = Math.max(
        1,
        (battleStats?.wave ?? 0) + (bossDefeated ? 5 : 0) + (stageCleared ? 3 : 0),
      );
      // 永久 meta 金幣:刻意不慷慨(computeMetaCoins,§4)。隨清到第幾關 + 難度旗幟微幅放大。
      // 鐵則:玩遊戲軌貨幣,只寫 progress.metaCoins,絕不碰戰力/學習軌欄位。
      // difficultyTier 是 P1 難度旗幟欄位(ROGUELITE_ROADMAP §P1.5,尚未實作)→ 省略 = cozy(×1)。
      metaCoinsGained = computeMetaCoins({
        stageReached: stage ?? 1,
        stageCleared,
      });
      const progress = loadProgress();
      if (progress) {
        if (commanderGained > 0) {
          commanderResult = addCommanderXp(progress, commanderGained);
        }
        addWeaponShards(progress, shardsGained);
        if (metaCoinsGained > 0) {
          progress.metaCoins = (Number.isFinite(progress.metaCoins) ? (progress.metaCoins as number) : 0) + metaCoinsGained;
        }
        saveProgress(progress);
        postSkillLevels = progress.skillLevels ?? null;
      }
    }

    // ── 打贏魔王:首殺解鎖武器類型 + 魔王圖鑑(玩遊戲軌「廣度」,0 戰力寫入)──────
    // 鐵則(學習/遊戲分離,§7):只寫 unlockedWeapons / bossesDefeated(廣度),
    // **絕不** addFreeXP/addFactionXP/skillLevels/永久傷害。武器戰鬥效果走單場
    // RunModifiers(解鎖只是「該武器進三選一池」,run 中抽到才生效、跨場歸零)。
    // 冪等:重打同魔王不重複(recordWeaponUnlocked/recordBossDefeated 都去重),
    // 重打也不再彈「解鎖新武器」橫幅(只在「首殺新解鎖」時 newWeaponName 非空)。
    let newWeaponName: string | null = null;
    // R-005 P3 — 本場新解鎖的「行為主武器」顯示名(打魔王給 / 戰後寶箱開出,首次才非空)。
    let newMainWeaponName: string | null = null;
    let bossCodexCount = 0;
    if (outcome === 'victory' && bossDefeated && bossId) {
      try {
        const progress = loadProgress();
        if (progress) {
          recordBossDefeated(progress, bossId);
          const weaponId = weaponUnlockForBoss(bossId);
          if (weaponId && recordWeaponUnlocked(progress, weaponId)) {
            newWeaponName = weaponTypeById(weaponId)?.displayName ?? null;
          }
          // R-005 P3 (B) 打魔王給主武器:首殺額外永久解鎖一把行為主武器(registry 6 把)。
          // 鐵則:同 weapon-* 解鎖,只寫 unlockedWeapons(廣度),0 戰力寫入。冪等:已擁有
          // (含起手 rapid-cannon/railgun)→ recordWeaponUnlocked 回 false,不再彈橫幅。
          const mainId = mainWeaponForBoss(bossId);
          if (mainId && recordWeaponUnlocked(progress, mainId)) {
            newMainWeaponName = weaponMetaById(mainId)?.displayName ?? null;
          }
          saveProgress(progress);
          bossCodexCount = progress.bossesDefeated?.length ?? 0;
        }
      } catch { /* headless / no localStorage — 解鎖失敗不影響其餘結算 */ }
    }

    // ── R-005 P3 (D) 戰後寶箱:勝利機率永久解鎖一把「尚未擁有」的行為主武器 ──────
    // 鐵則(學習/遊戲分離):只寫 unlockedWeapons(廣度),**絕不**碰戰力欄位。
    // victoryChestUnlock 是純函式(注入 Math.random),已全收集 → 回 null(不彈)。
    // 打魔王時機率加成。與上面 boss 主武器各自 record,皆冪等;若兩者同一把,先到先得。
    if (outcome === 'victory') {
      try {
        const progress = loadProgress();
        if (progress) {
          const chestId = victoryChestUnlock(progress.unlockedWeapons, !!bossDefeated);
          if (chestId && recordWeaponUnlocked(progress, chestId)) {
            // 已被 boss 主武器解鎖時 newMainWeaponName 已有值;寶箱只在「真的新解鎖」時覆寫。
            newMainWeaponName = weaponMetaById(chestId)?.displayName ?? newMainWeaponName;
            saveProgress(progress);
          }
        }
      } catch { /* headless / no localStorage — 寶箱解鎖失敗不影響其餘結算 */ }
    }

    // ── 闖關地圖過關結算(玩遊戲軌「廣度」)─────────────────────────────────
    // 戰鬥勝利 + 本場屬某個闖關關卡(active-campaign-stage flag 由 CampaignMapScene
    // 設定)→ 標記該關 cleared + 評星(依 hpRatioAtWin)+ (boss 關)解鎖配件。
    // markStageCleared **只**寫 battleCampaign / unlockedAccessories,絕不碰戰力欄位
    // (鐵則)。consume-on-read:讀完即清,避免漏到下一場非闖關戰鬥。
    // 非闖關入口(TitleScene / debug / 塔)永遠 null → 此區塊整段 no-op,維持原行為。
    let campaignStageId: string | null = null;
    let campaignResult: StageClearResult | null = null;
    if (outcome === 'victory') {
      campaignStageId = consumeActiveCampaignStage();
      if (campaignStageId) {
        try {
          const progress = loadProgress();
          if (progress) {
            campaignResult = markStageCleared(
              progress,
              campaignStageId,
              typeof hpRatioAtWin === 'number' ? hpRatioAtWin : 0,
            );
            saveProgress(progress);
          }
        } catch { /* headless / no localStorage — 闖關標記失敗不影響其餘結算 */ }
      }
    } else {
      // 戰敗也要清掉 flag,否則下次重打別關時舊 flag 殘留會誤標(consume-on-read)。
      try { consumeActiveCampaignStage(); } catch { /* noop */ }
    }
    /**
     * 回闖關地圖。IA 改版(boss 2026-06-14):闖關地圖 = 唯一主入口,「選門大廳」已停用。
     * 闖關戰鬥帶 justClearedStageId 做亮燈/解鎖;非闖關場也一律回闖關地圖(不再回 HubScene)。
     */
    const goToCampaignOrHub = (dispatched: { v: boolean }): void => {
      if (dispatched.v) return;
      dispatched.v = true;
      try {
        if (campaignStageId) {
          this.scene.start('CampaignMapScene', { justClearedStageId: campaignStageId });
        } else {
          this.scene.start('CampaignMapScene');
        }
      } catch { /* headless */ }
      try { window.dispatchEvent(new CustomEvent('ef:return-hub')); } catch { /* noop */ }
    };

    // VICTORY-CELEBRATION (task #34): trigger confetti + emblem + level-up
    // floats on any victory path BEFORE rendering the text/buttons so they
    // layer underneath.
    if (outcome === 'victory') {
      this._fireVictoryFx(!!bossDefeated, postSkillLevels, prevSkillLevels);
    }

    // Boss-defeated special treatment
    if (bossDefeated) {
      // R-004 A5 — 先讀上一場(profile 專屬鍵)再覆蓋,才能比「本場 vs 上次」。
      const prevBattle = loadLastBattleStats();
      // Persist battle stats on boss-victory path
      if (battleStats) {
        saveBattleStats({ ...battleStats, timestamp: Date.now() });
      }

      // 結算重設計(boss 2026-06-14, 老闆截圖回報「全疊在同一垂直帶、糊成一團」):
      // 改用 computeResultSections 把畫面切成「不重疊」的垂直分區帶 —— 標題 / 星等 /
      // 動態獎勵卡列表 / 指揮官 XP 條 / 統計格 / 底部 CTA。獎勵卡列表依實際解鎖項數
      // 動態決定帶高,項目多時自動壓縮間距、封頂顯示,不再互相蓋字或溢出。
      const cx = GAME.width / 2;
      const panelW = GAME.width - 96;   // 卡片寬(獎勵卡 / 內容對齊用)
      const panelLeft = cx - panelW / 2;
      const accent = (hubFactionId != null && FACTION_BURST_COLOR[hubFactionId] != null)
        ? FACTION_BURST_COLOR[hubFactionId]!
        : FACTION_BURST_FALLBACK_COLOR;

      // ── 1) 先盤點本場「獎勵列表」項目(動態 0..N),才能算獎勵帶高度 ──────────────
      const rewardItems: Array<{ icon: string; text: string; color: string }> = [];
      if (newWeaponName) {
        rewardItems.push({ icon: '🔫', text: `解鎖新武器：${newWeaponName}`, color: '#80d8ff' });
      }
      if (newMainWeaponName) {
        rewardItems.push({ icon: '🗡️', text: `取得主武器：${newMainWeaponName}`, color: '#a5d6a7' });
      }
      if (bossCodexCount > 0) {
        rewardItems.push({ icon: '🏆', text: `魔王圖鑑　${bossCodexCount}/${TOTAL_BOSS_COUNT}`, color: '#ffe082' });
      }
      if (upgradedLevels) {
        const SUBJECT_LABEL: Record<string, string> = {
          math: '數學', chinese: '國語', english: '英語', nature: '自然', social: '社會',
        };
        const lbl = upgradedLevels.subject ? (SUBJECT_LABEL[upgradedLevels.subject] ?? '段位') : '段位';
        // boss memory「段位 from→to」測試需含中文科目 + from + to;此處字串維持該契約。
        rewardItems.push({
          icon: '🎖️',
          text: `${lbl} ${upgradedLevels.fromTier} → ${upgradedLevels.toTier} ★`,
          color: '#ffe082',
        });
      }
      // 指揮官升等解鎖(英雄/造型/關卡/模式 + 封頂 QoL)— 玩遊戲軌廣度,做成一張獎勵卡。
      if (commanderResult?.leveledUp) {
        const u = commanderResult.unlocked;
        const HERO_L: Record<string, string> = { celestial: '星算俠', runic: '符文守', techno: '機械斥', elemental: '自然御', dominion: '疆域王' };
        const COS_L: Record<string, string> = { 'tank-gold': '黃金塗裝', 'tank-rainbow': '彩虹塗裝' };
        const MODE_L: Record<string, string> = { 'infinite-tower': '無限魔王塔', extreme: '極限挑戰' };
        const QOL_L: Record<string, string> = { startingCoins: '起手金幣', rerollCharges: '重抽次數' };
        const names = [
          ...u.heroes.map((h) => HERO_L[h] ?? h),
          ...u.cosmetics.map((c) => COS_L[c] ?? c),
          ...u.modes.map((m) => MODE_L[m] ?? m),
          ...u.stages.map((s) => `Stage ${s}`),
          ...u.qol.map((q) => QOL_L[q] ?? q),
        ];
        const summary = names.length > 0
          ? `指揮官 Lv ${commanderResult.levelAfter}　解鎖 ${names.join('、')}`
          : `升上指揮官 Lv ${commanderResult.levelAfter}`;
        rewardItems.push({ icon: '⭐', text: summary, color: '#ffe082' });
      }

      // 獎勵項封頂:超過可見上限時收成「…還有 N 項」,確保獎勵帶不溢出。
      let overflowNote: string | null = null;
      if (rewardItems.length > REWARDS_MAX_VISIBLE) {
        const extra = rewardItems.length - (REWARDS_MAX_VISIBLE - 1);
        rewardItems.splice(REWARDS_MAX_VISIBLE - 1);
        overflowNote = `…還有 ${extra} 項獎勵`;
        rewardItems.push({ icon: '➕', text: overflowNote, color: '#9fb3d8' });
      }

      // ── 2) 分區帶(依內容量自適應,彼此不重疊)──────────────────────────────────
      const hasCampaign = !!(campaignStageId && campaignResult);
      const sections = computeResultSections({
        screenH: GAME.height,
        rewardCount: rewardItems.length,
        hasStars: hasCampaign,
        hasStarRule: false, // 評星規則由 _showCampaignClear 內部隨星等一起排(stars 帶已留高)
        hasStats: !!battleStats,
      });

      // ── 全幅暗底(depth 0):蓋住底下殘留的戰鬥場景,確保結算內容對比清楚 ────────────
      this.add.rectangle(cx, GAME.height / 2, GAME.width, GAME.height, 0x060a16, 0.92).setDepth(0);
      // ── 背板卡:深色 + 門派色厚描邊(卡通風),內容浮在卡上 ──────────────────────────
      this.add.rectangle(cx, GAME.height / 2, GAME.width - 56, GAME.height - 40, 0x0c1430, 0.96)
        .setStrokeStyle(4, accent, 0.95).setDepth(1);

      // ── 標題帶 ────────────────────────────────────────────────────────────────
      const titleStr = hasCampaign ? '通關！' : '征服永恆要塞!';
      // 標題置於帶上段、副標置於帶下段,用 band top 定錨確保兩者有固定間距不貼字。
      const titleY = bossName ? sections.title.top + 26 : sections.title.y;
      const titleText = this.add.text(cx, titleY, titleStr, {
        fontSize: '44px', color: '#ffd54f', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(20);
      this._revealRow(titleText, titleY, 0);
      // boss 名:標題下方小一號副標(同帶內,不另佔帶 → 不擠壓下方)。
      if (bossName) {
        const subY = sections.title.top + 58;
        const bossNameText = this.add.text(cx, subY, bossName, {
          fontSize: '17px', color: '#d8ccff', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(20);
        this._revealRow(bossNameText, subY, 120);
      }

      // ── 星等帶(闖關過關才有):三星逐顆登場 + 評星規則 + 永久配件 ──────────────────
      // _showCampaignClear 內部由 y 往下排 stars / rule(+26) / accessory(+48),
      // stars 帶已配足高度容納整個子堆疊,不會壓到獎勵帶。
      if (hasCampaign) {
        this._showCampaignClear(campaignResult!, cx, sections.stars.top + 18, hpRatioAtWin);
      }

      // ── 獎勵卡帶:動態垂直列表(每項一張卡,左圖右文,依序冒出)──────────────────
      if (rewardItems.length > 0) {
        const rb = sections.rewards;
        const rowH = rb.height / Math.max(1, rewardItems.length);
        rewardItems.forEach((it, i) => {
          const y = rb.top + rowH * (i + 0.5);
          this._addRewardItem(panelLeft, y, panelW, it.icon, it.text, it.color, 200 + i * 110, accent);
        });
      }

      // ── 指揮官 XP 帶:進度條(本場 +gained、目前 Lv、升等高亮)──────────────────────
      const xpTotal = (commanderResult?.gained != null)
        ? (() => { try { return loadProgress()?.commanderXp ?? commanderGained; } catch { return commanderGained; } })()
        : commanderGained;
      this._renderCommanderXpBar(
        cx, sections.xpBar.y + 10, xpTotal,
        commanderResult?.levelAfter ?? 1,
        !!commanderResult?.leveledUp, accent,
      );
      // 隱藏式 XP 明細:tap-to-expand 的「+N 指揮官經驗 ▼」當 XP 帶的小標(置於條上方,
      // 不與 Lv 標籤撞)。維持家長透明度 + 既有測試需要的「指揮官經驗 ▼」文字契約。
      const xpText = this.add.text(cx, sections.xpBar.top + 2, `+0 指揮官經驗  ▼`, {
        fontSize: '15px', color: '#80cbc4', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(19).setInteractive();
      this._animateCountUp(xpText, commanderGained, '+', ' 指揮官經驗  ▼', 800, () => {
        this._flyXPToGauge(cx, sections.xpBar.y, commanderGained);
      });
      this._addXPBreakdownToggle(
        xpText,
        xpBreakdown ?? this._defaultBreakdown(commanderGained, bossDefeated ?? false),
        cx, sections.xpBar.y + 40,
      );

      // ── 統計帶:波次 / 消滅 / 得分,三欄等寬大字 ────────────────────────────────
      if (battleStats) {
        this._renderStatsGrid(cx, sections.stats.y, battleStats);
        // R-004 A5 — 「本場 vs 上次」對比(史上第一場 → 不顯示)。做成統計格下緣一行。
        const cmp = buildStatComparisons(battleStats, prevBattle);
        if (cmp.length > 0) {
          const parts = cmp
            .filter((r) => r.direction !== 'same')
            .map((r) => `${r.label} ${r.arrow}${Math.abs(r.delta)}`);
          if (parts.length > 0) {
            this.add.text(cx, sections.stats.top + sections.stats.height + 8, `本場 vs 上次　${parts.join('　')}`, {
              fontSize: '14px', color: '#90a4ae', fontFamily: 'sans-serif',
            }).setOrigin(0.5).setDepth(19);
          }
        }
      }
      // mastery box / 比上次高分:沿用既有 helper(短暫浮現後淡出,不佔固定版位)。
      // 錨在 CTA 帶上緣縫隙,內容少時才出現,飄一下就消失,不擋按鈕。
      this._animateMasteryBoxUpgrade(upgradedBoxes ?? [], cx, sections.cta.top - 28, hubFactionId);
      if (
        hubFactionId != null && battleStats && typeof battleStats.score === 'number'
        && typeof prevScore === 'number'
      ) {
        this._showScoreDelta(battleStats.score, prevScore, cx, sections.cta.top - 16);
      }

      const dispatched = { v: false };
      // 闖關戰鬥 → 「回地圖」;非闖關 → 沿用原「回 hub」(markFactionConquered)。
      const goHome = () => goToCampaignOrHub(dispatched);
      const goNext = () => {
        if (dispatched.v) return;
        dispatched.v = true;
        // 下一關:stage+1 遞進難度(battle scene 的 stageMul 吃 stage),沿用同主題。
        // 內部 scene.start 不走 main.ts setMode → 補發 ef:enter-battle 重掛技能按鈕
        // (戰鬥場景轉結算時 remove 了 .touch-controls)。
        try { window.dispatchEvent(new CustomEvent('ef:enter-battle')); } catch { /* noop */ }
        this.scene.start('HorizontalBattleScene', {
          stage: (stage ?? 1) + 1,
          ...(hubFactionId != null ? { hubFactionId } : {}),
        });
      };

      // CTA 雙按鈕(主「回 hub/地圖」/ 次「下一關」)錨在底部 cta 帶,band 已預留
      // safe-area 高度(ctaH=134),手機瀏海/圓角不會切到按鈕。闖關時主鈕標「回地圖」。
      const ctaBand = sections.cta;
      const hasNext = !campaignStageId;
      // 有「下一關」時:主鈕在上、次鈕在下;只有主鈕時主鈕略往下置中。
      const homeY = hasNext ? ctaBand.top + 40 : ctaBand.top + 52;
      const homeLabel = campaignStageId ? '回地圖' : '回地圖';
      const btnHomeBg = this.add.rectangle(cx, homeY, 300, 58, 0xffd54f)
        .setDepth(10)
        .setInteractive({ useHandCursor: true });
      btnHomeBg.setStrokeStyle(3, 0xffffff, 1);
      this.add.text(cx, homeY, homeLabel, {
        fontSize: '25px', color: '#0a0f1e', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(11);
      btnHomeBg.on('pointerdown', goHome);

      // 闖關戰鬥:不顯示「下一關(無限遞增難度)」按鈕 —— 闖關推進由地圖驅動,
      // 避免兩套關卡推進語意打架。非闖關(隨機/hub)維持原「下一關」CTA。
      let btnNextBg: Phaser.GameObjects.Rectangle | null = null;
      if (hasNext) {
        const nextY = ctaBand.top + 96;
        btnNextBg = this.add.rectangle(cx, nextY, 240, 44, 0x1565c0)
          .setDepth(10)
          .setStrokeStyle(2, 0x90caf9, 0.9)
          .setInteractive({ useHandCursor: true });
        this.add.text(cx, nextY, `下一關 (第 ${(stage ?? 1) + 1} 關)`, {
          fontSize: '19px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(11);
        btnNextBg.on('pointerdown', goNext);
      }

      // Keep keyboard + global pointer fallback so existing flows still work
      this.input.keyboard!.once('keydown', goHome);
      this.input.once('pointerdown', goHome);
      this.time.delayedCall(8000, goHome);
      this._registerResultCTAs(
        campaignStageId ? { '回地圖': goHome } : { '回地圖': goHome, '下一關': goNext },
      );
      void btnHomeBg; void btnNextBg;
    } else if (outcome === 'victory') {
      // R-004 A5 — 先讀上一場(profile 專屬鍵)再覆蓋,才能比「本場 vs 上次」。
      const prevBattle = loadLastBattleStats();
      // Persist battle stats on normal-victory path
      if (battleStats) {
        saveBattleStats({ ...battleStats, timestamp: Date.now() });
      }

      // 結算重設計(boss 2026-06-14)— 一般勝利沿用 boss 勝利的「分區帶」排版,
      // 避免標題/消滅/經驗/對比/CTA 互相重疊(舊版硬編 GAME.height/2 偏移會疊字)。
      const cx = GAME.width / 2;
      const panelW = GAME.width - 96;
      const panelLeft = cx - panelW / 2;
      const accent = (hubFactionId != null && FACTION_BURST_COLOR[hubFactionId] != null)
        ? FACTION_BURST_COLOR[hubFactionId]!
        : 0x80cbc4;

      // 動態獎勵列表(一般勝利:寶箱主武器 / 段位 / 指揮官升等)。
      const rewardItems: Array<{ icon: string; text: string; color: string }> = [];
      if (newMainWeaponName) {
        rewardItems.push({ icon: '🎁', text: `寶箱取得主武器：${newMainWeaponName}`, color: '#a5d6a7' });
      }
      if (upgradedLevels) {
        const SUBJECT_LABEL: Record<string, string> = {
          math: '數學', chinese: '國語', english: '英語', nature: '自然', social: '社會',
        };
        const lbl = upgradedLevels.subject ? (SUBJECT_LABEL[upgradedLevels.subject] ?? '段位') : '段位';
        rewardItems.push({
          icon: '🎖️',
          text: `${lbl} ${upgradedLevels.fromTier} → ${upgradedLevels.toTier} ★`,
          color: '#ffe082',
        });
      }
      if (commanderResult?.leveledUp) {
        const u = commanderResult.unlocked;
        const HERO_L: Record<string, string> = { celestial: '星算俠', runic: '符文守', techno: '機械斥', elemental: '自然御', dominion: '疆域王' };
        const COS_L: Record<string, string> = { 'tank-gold': '黃金塗裝', 'tank-rainbow': '彩虹塗裝' };
        const MODE_L: Record<string, string> = { 'infinite-tower': '無限魔王塔', extreme: '極限挑戰' };
        const QOL_L: Record<string, string> = { startingCoins: '起手金幣', rerollCharges: '重抽次數' };
        const names = [
          ...u.heroes.map((h) => HERO_L[h] ?? h), ...u.cosmetics.map((c) => COS_L[c] ?? c),
          ...u.modes.map((m) => MODE_L[m] ?? m), ...u.stages.map((s) => `Stage ${s}`),
          ...u.qol.map((q) => QOL_L[q] ?? q),
        ];
        rewardItems.push({
          icon: '⭐',
          text: names.length > 0 ? `指揮官 Lv ${commanderResult.levelAfter}　解鎖 ${names.join('、')}` : `升上指揮官 Lv ${commanderResult.levelAfter}`,
          color: '#ffe082',
        });
      }
      if (rewardItems.length > REWARDS_MAX_VISIBLE) {
        const extra = rewardItems.length - (REWARDS_MAX_VISIBLE - 1);
        rewardItems.splice(REWARDS_MAX_VISIBLE - 1);
        rewardItems.push({ icon: '➕', text: `…還有 ${extra} 項獎勵`, color: '#9fb3d8' });
      }

      const hasCampaign = !!(campaignStageId && campaignResult);
      const sections = computeResultSections({
        screenH: GAME.height,
        rewardCount: rewardItems.length,
        hasStars: hasCampaign,
        hasStarRule: false,
        hasStats: !!battleStats,
      });

      // 全幅暗底 + 背板卡(同 boss 勝利,避免底下殘留場景透出)。
      this.add.rectangle(cx, GAME.height / 2, GAME.width, GAME.height, 0x060a16, 0.92).setDepth(0);
      this.add.rectangle(cx, GAME.height / 2, GAME.width - 56, GAME.height - 40, 0x0c1430, 0.96)
        .setStrokeStyle(4, accent, 0.95).setDepth(1);

      // 標題帶。
      const titleStr = hasCampaign ? '通關！' : '勝利！';
      const titleText = this.add.text(cx, sections.title.y, titleStr, {
        fontSize: '46px', color: '#80cbc4', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(20);
      this._revealRow(titleText, sections.title.y, 0);

      // 星等帶(普通關 wave-clear 也可能闖關)。
      if (hasCampaign) {
        this._showCampaignClear(campaignResult!, cx, sections.stars.top + 18, hpRatioAtWin);
      }

      // 獎勵卡帶。
      if (rewardItems.length > 0) {
        const rb = sections.rewards;
        const rowH = rb.height / Math.max(1, rewardItems.length);
        rewardItems.forEach((it, i) => {
          this._addRewardItem(panelLeft, rb.top + rowH * (i + 0.5), panelW, it.icon, it.text, it.color, 200 + i * 110, accent);
        });
      }

      // 指揮官 XP 帶。
      const xpTotal = (commanderResult?.gained != null)
        ? (() => { try { return loadProgress()?.commanderXp ?? commanderGained; } catch { return commanderGained; } })()
        : commanderGained;
      this._renderCommanderXpBar(
        cx, sections.xpBar.y + 10, xpTotal, commanderResult?.levelAfter ?? 1,
        !!commanderResult?.leveledUp, accent,
      );
      const xpText2 = this.add.text(cx, sections.xpBar.top + 2, `+0 指揮官經驗  ▼`, {
        fontSize: '15px', color: '#80cbc4', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(19).setInteractive();
      this._animateCountUp(xpText2, commanderGained, '+', ' 指揮官經驗  ▼', 800, () => {
        this._flyXPToGauge(cx, sections.xpBar.y, commanderGained);
      });
      this._addXPBreakdownToggle(xpText2, xpBreakdown ?? this._defaultBreakdown(commanderGained, false), cx, sections.xpBar.y + 40);

      // 統計帶 + 對比。
      if (battleStats) {
        this._renderStatsGrid(cx, sections.stats.y, battleStats);
        const cmp = buildStatComparisons(battleStats, prevBattle);
        const parts = cmp.filter((r) => r.direction !== 'same').map((r) => `${r.label} ${r.arrow}${Math.abs(r.delta)}`);
        if (parts.length > 0) {
          this.add.text(cx, sections.stats.top + sections.stats.height + 8, `本場 vs 上次　${parts.join('　')}`, {
            fontSize: '14px', color: '#90a4ae', fontFamily: 'sans-serif',
          }).setOrigin(0.5).setDepth(19);
        }
      }
      this._animateMasteryBoxUpgrade(upgradedBoxes ?? [], cx, sections.cta.top - 28, hubFactionId);
      if (
        hubFactionId != null && battleStats && typeof battleStats.score === 'number'
        && typeof prevScore === 'number'
      ) {
        this._showScoreDelta(battleStats.score, prevScore, cx, sections.cta.top - 16);
      }

      const dispatched = { v: false };
      const goHome = () => goToCampaignOrHub(dispatched);
      const goNext = () => {
        if (dispatched.v) return;
        dispatched.v = true;
        // 下一關:stage+1 遞進難度(battle scene 的 stageMul 吃 stage),沿用同主題。
        // 內部 scene.start 不走 main.ts setMode → 補發 ef:enter-battle 重掛技能按鈕。
        try { window.dispatchEvent(new CustomEvent('ef:enter-battle')); } catch { /* noop */ }
        this.scene.start('HorizontalBattleScene', {
          stage: (stage ?? 1) + 1,
          ...(hubFactionId != null ? { hubFactionId } : {}),
        });
      };

      // CTA 雙按鈕,錨在底部 cta 帶(safe-area 已預留)。
      const ctaBand = sections.cta;
      const hasNext = !campaignStageId;
      const homeY = hasNext ? ctaBand.top + 40 : ctaBand.top + 52;
      const homeLabel = campaignStageId ? '回地圖' : '回地圖';
      const btnHomeBg = this.add.rectangle(cx, homeY, 300, 58, 0x80cbc4)
        .setDepth(10)
        .setInteractive({ useHandCursor: true });
      btnHomeBg.setStrokeStyle(3, 0xffffff, 1);
      this.add.text(cx, homeY, homeLabel, {
        fontSize: '25px', color: '#0a0f1e', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(11);
      btnHomeBg.on('pointerdown', goHome);

      let btnNextBg: Phaser.GameObjects.Rectangle | null = null;
      if (hasNext) {
        const nextY = ctaBand.top + 96;
        btnNextBg = this.add.rectangle(cx, nextY, 240, 44, 0x1565c0)
          .setDepth(10)
          .setStrokeStyle(2, 0x90caf9, 0.9)
          .setInteractive({ useHandCursor: true });
        this.add.text(cx, nextY, `下一關 (第 ${(stage ?? 1) + 1} 關)`, {
          fontSize: '19px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(11);
        btnNextBg.on('pointerdown', goNext);
      }

      // Keep keyboard + global pointer fallback so existing flows still work
      this.input.keyboard!.once('keydown', goHome);
      this.input.once('pointerdown', goHome);
      this.time.delayedCall(8000, goHome);
      this._registerResultCTAs(
        campaignStageId ? { '回地圖': goHome } : { '回地圖': goHome, '下一關': goNext },
      );
      void btnHomeBg; void btnNextBg;
    } else {
      // Defeat path: load previous stats, then save current
      const lastStats = loadLastBattleStats();
      if (battleStats) {
        saveBattleStats({ ...battleStats, timestamp: Date.now() });
      }

      // Dark background
      this.add.rectangle(GAME.width / 2, GAME.height / 2, GAME.width, GAME.height, 0x0a0a1a, 0.95).setDepth(-1);

      // Title — "再次出發!" in cyan (not red, not "失敗")
      this.add.text(GAME.width / 2, GAME.height / 2 - 160, '再次出發!', {
        fontSize: '72px', color: '#80cbc4', fontFamily: 'sans-serif',
      }).setOrigin(0.5);

      // Stats row
      if (battleStats) {
        this.add.text(
          GAME.width / 2, GAME.height / 2 - 80,
          `第 ${battleStats.wave} 波  |  消滅 ${battleStats.killed} 隻  |  撐了 ${battleStats.survivedSec} 秒`,
          { fontSize: '24px', color: '#ffd54f', fontFamily: 'sans-serif' },
        ).setOrigin(0.5);

        // Progress comparison
        if (lastStats) {
          const delta = battleStats.survivedSec - lastStats.survivedSec;
          let progressText: string;
          let progressColor: string;
          if (delta > 0) {
            progressText = `+${delta} 秒!進步了!`;
            progressColor = '#69f0ae';
          } else if (delta < 0) {
            progressText = '繼續加油,你做得到!';
            progressColor = '#80cbc4';
          } else {
            progressText = '保持住 — 再突破!';
            progressColor = '#ffffff';
          }
          this.add.text(GAME.width / 2, GAME.height / 2 - 40, progressText, {
            fontSize: '20px', color: progressColor, fontFamily: 'sans-serif',
          }).setOrigin(0.5);

          // Personalized encouraging phrase when improvement
          if (delta > 0) {
            this.add.text(
              GAME.width / 2, GAME.height / 2 + 10,
              `指揮官,你撐了 ${battleStats.survivedSec} 秒!比上次多了 ${delta} 秒!`,
              { fontSize: '18px', color: '#ccc', fontFamily: 'sans-serif' },
            ).setOrigin(0.5);
          } else {
            const phrase = ENCOURAGE_PHRASES[Math.floor(Math.random() * ENCOURAGE_PHRASES.length)];
            this.add.text(GAME.width / 2, GAME.height / 2 + 10, phrase, {
              fontSize: '18px', color: '#ccc', fontFamily: 'sans-serif',
            }).setOrigin(0.5);
          }
        } else {
          const phrase = ENCOURAGE_PHRASES[Math.floor(Math.random() * ENCOURAGE_PHRASES.length)];
          this.add.text(GAME.width / 2, GAME.height / 2 + 10, phrase, {
            fontSize: '18px', color: '#ccc', fontFamily: 'sans-serif',
          }).setOrigin(0.5);
        }
      } else {
        // No battleStats: just show a phrase
        const phrase = ENCOURAGE_PHRASES[Math.floor(Math.random() * ENCOURAGE_PHRASES.length)];
        this.add.text(GAME.width / 2, GAME.height / 2 + 10, phrase, {
          fontSize: '18px', color: '#ccc', fontFamily: 'sans-serif',
        }).setOrigin(0.5);
      }

      // W8 PR-3 — 失敗 CTA 雙按鈕(主「重試」/ 次「回 hub」)。
      // 移除原本「先學一章節」按鈕:結算畫面不得暗示學習路由(boss memory
      // feedback_learn_pro_mode + 學習/遊戲分離鐵則)。
      const btnRetryBg = this.add.rectangle(GAME.width / 2, GAME.height / 2 + 70, 260, 52, 0x2e7d32)
        .setDepth(10).setInteractive();
      this.add.text(GAME.width / 2, GAME.height / 2 + 70, '重試', {
        fontSize: '22px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(11);
      const goRetry = () => { try { window.dispatchEvent(new CustomEvent('ef:enter-battle')); } catch { /* noop */ } try { this.scene.start('HorizontalBattleScene'); } catch { /* headless */ } };
      btnRetryBg.on('pointerdown', goRetry);
      btnRetryBg.on('pointerup', goRetry);

      const btnHubBg = this.add.rectangle(GAME.width / 2, GAME.height / 2 + 135, 220, 44, 0x37474f)
        .setDepth(10).setInteractive();
      this.add.text(GAME.width / 2, GAME.height / 2 + 135, '回地圖', {
        fontSize: '18px', color: '#cccccc', fontFamily: 'sans-serif',
      }).setOrigin(0.5).setDepth(11);
      const goHubDefeat = () => {
        // IA 改版(boss 2026-06-14):敗北 → 回闖關地圖(選門大廳已停用)。
        // 仍 dispatch ef:return-hub 同步 main.ts mode 記帳。
        try { this.scene.start('CampaignMapScene'); } catch { /* headless */ }
        try { window.dispatchEvent(new CustomEvent('ef:return-hub')); } catch { /* noop */ }
      };
      btnHubBg.on('pointerdown', goHubDefeat);
      btnHubBg.on('pointerup', goHubDefeat);

      // v1.1 battle-flow P0: expose defeat CTAs for mobile e2e (verifies 死掉→
      // 重試 / 回地圖 really work). See _registerResultCTAs.
      this._registerResultCTAs({ '重試': goRetry, '回地圖': goHubDefeat });
      // Suppress unused-var warnings
      void btnRetryBg; void btnHubBg;
    }
  }

  /**
   * v1.1 battle-flow P0: expose the rendered CTA handlers on window.__resultCTAs
   * so mobile-viewport e2e can prove the buttons exist AND invoke the exact same
   * fn the pointer handlers fire. Dev/E2E only; no-op without window.
   */
  private _registerResultCTAs(ctas: Record<string, () => void>): void {
    try {
      const w = window as unknown as { __resultCTAs?: Record<string, () => void> };
      w.__resultCTAs = { ...(w.__resultCTAs ?? {}), ...ctas };
    } catch { /* no window */ }
  }
}
